import React, { useState } from "react";
import { NoteContainer, NoteDate, NoteHeader, NoteName, NoteText, NotesCardStatus } from "../TableTab.styled";
import moment from "moment";
import WarningAmber from "@mui/icons-material/WarningAmber";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, CircularProgress } from "@mui/material";

// Keywords that mark an alert as an actionable alert. Clicking:
//   • intake       → opens the client's intake form
//   • assignment   → opens the client on the Assignments section
//   • levelofcare  → opens the client and scrolls to the Level of Care field
// (see onAlertAction). Mirrors NotesPanel / ClientInfo alert rules.
const INTAKE_KEYWORDS = ['intake not complete', 'intakenotcomplete', 'intake assessment'];
const ASSIGNMENT_KEYWORDS = ['no active client assignment', 'no active assignment', 'no assignment', 'clientassignment', 'client assignment'];
const LEVEL_OF_CARE_KEYWORDS = ['level of care not set', 'no level of care', 'level of care', 'levelofcare'];
const norm = (s) => (s || '').toLowerCase().replace(/[\s_\-]+/g, '');
const alertText = (note) => norm([note.alertCode, note.message, note.details, note.text].filter(Boolean).join(' '));
const isIntakeAlert = (note) =>
  note?.type === 'Alert' && INTAKE_KEYWORDS.some((kw) => alertText(note).includes(norm(kw)));
const isAssignmentAlert = (note) =>
  note?.type === 'Alert' && ASSIGNMENT_KEYWORDS.some((kw) => alertText(note).includes(norm(kw)));
const isLevelOfCareAlert = (note) =>
  note?.type === 'Alert' && LEVEL_OF_CARE_KEYWORDS.some((kw) => alertText(note).includes(norm(kw)));
// Which action, if any, this alert triggers.
const alertActionKind = (note) =>
  isIntakeAlert(note) ? 'intake'
    : isAssignmentAlert(note) ? 'assignment'
      : isLevelOfCareAlert(note) ? 'levelofcare'
        : null;
// Display priority when more than one actionable alert is competing for a slot — intake
// (red) always outranks the orange onboarding alerts, since completing intake is the most
// urgent action. A brand-new client can get all three alerts simultaneously; without this,
// intake's slot depended on an incidental SortDate tiebreak and could get pushed out.
const ACTION_PRIORITY = { intake: 2, assignment: 1, levelofcare: 1 };
const actionPriority = (note) => ACTION_PRIORITY[alertActionKind(note)] || 0;
// Per-action accent colour + background for the alert card. Only the intake alert keeps
// the red actionable styling; the others use the regular yellow alert colour (they stay
// clickable, indicated by the arrow icon).
const ACTION_STYLE = {
  intake: { accent: 'red', bg: '#FED9CC' },
  assignment: { accent: '#FF9800', bg: '#FFF8E1' },
  levelofcare: { accent: '#FF9800', bg: '#FFF8E1' },
};

export const NotesGridField = (props) => {
  const { types, notes, onAlertAction, onNoteClick } = props;
  const [loadingNoteId, setLoadingNoteId] = useState(null);

  // Support both old array format and new { items, totalCount } format
  const items = Array.isArray(notes) ? notes : (notes?.items ?? []);

  // Surface actionable alerts first (highest priority first) so they're reliably visible +
  // clickable, instead of losing a display slot to a lower-priority alert on an incidental tie.
  const ordered = [...items].sort(
    (a, b) => actionPriority(b) - actionPriority(a),
  );

  const getNameById = id => {
    const foundItem = types?.find(item => item.id === id);
    return foundItem ? foundItem.name : null;
  };

  return (
    <>
      {ordered.slice(0, 2).map((note, idx) => {
        const isAlert = note.type === 'Alert';
        // An alert is an "action" alert when we know how to act on it (open the intake
        // form, or open the client on the Assignments section).
        const actionKind = isAlert ? alertActionKind(note) : null;
        const actionable = !!actionKind && typeof onAlertAction === 'function';
        // Assignment + level-of-care alerts navigate elsewhere (arrow icon); intake adds.
        const isNavAction = actionKind === 'assignment' || actionKind === 'levelofcare';
        const { accent, bg: actionBg } = ACTION_STYLE[actionKind] || ACTION_STYLE.intake;
        const isLoading = loadingNoteId === (note.id || idx);
        // Regular (non-alert) notes are clickable to open in the notes drawer
        // (edit mode, or read-only for system notes).
        const noteClickable = !isAlert && typeof onNoteClick === 'function';

        const handleActionClick = async () => {
          if (isLoading) return;
          const noteKey = note.id || idx;
          setLoadingNoteId(noteKey);
          try {
            await onAlertAction(note, actionKind);
          } finally {
            setLoadingNoteId(null);
          }
        };
        return (
          <NoteContainer
            key={note.id || idx}
            onClick={actionable ? handleActionClick : (noteClickable ? () => onNoteClick(note) : undefined)}
            style={{
              ...(isAlert
                ? {
                    background: actionable ? actionBg : '#FFF8E1',
                    borderLeft: `3px solid ${actionable ? accent : '#FF9800'}`,
                  }
                : {}),
              ...((actionable || noteClickable) ? { cursor: isLoading ? 'default' : 'pointer' } : {}),
              position: 'relative',
            }}
          >
            {/* Loading overlay */}
            {isLoading && (
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '4px',
                bgcolor: 'rgba(255,255,255,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
              }}>
                <CircularProgress size={20} sx={{ color: 'red' }} />
              </Box>
            )}
            <NoteHeader>
              {isAlert ? (
                <NoteName style={{ color: actionable ? accent : '#E65100', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <WarningAmber fontSize="small" sx={{ color: actionable ? accent : '#E65100' }} />
                  Alert
                </NoteName>
              ) : (
                <NoteName>{note?.owner?.firstName} {note?.owner?.lastName}</NoteName>
              )}
              {actionable && (
                isNavAction
                  ? <ArrowForwardIcon fontSize="small" sx={{ color: accent, ml: 'auto' }} />
                  : <AddCircleOutlineIcon fontSize="small" sx={{ color: accent, ml: 'auto' }} />
              )}
              {!isAlert && (
                <NotesCardStatus>
                  {getNameById(note?.quickNoteTypeId)}
                </NotesCardStatus>
              )}
            </NoteHeader>
            {!isAlert && (
              <NoteDate>{moment(note?.createDate).format("MM/DD/YYYY HH:mm")}</NoteDate>
            )}
            <NoteText>
              {isAlert ? (note.message || note.details || note.text) : note.text}
            </NoteText>
          </NoteContainer>
        );
      })}
    </>
  );
}
